import * as React from 'react'
import {
  FaIcon,
  faCalculator,
  faPrint,
  faArrowsRotate,
  faMoneyBillTrendUp,
  faCalendar,
  faTable,
  faCoins,
  faChartPieSimple,
  faClockRotateLeft,
  faClose,
  faTrash,
  faFloppyDisk,
  faCircleInfo,
  faChartBulletSolid,
} from '@/lib/fa'

import Tooltip from '@/modules/drivers/shared/ToolTip'
import TimesheetLeaveCounters from './TimesheetLeaveCounters'
import { TimesheetViewMode } from './TimesheetTableRow'
import type { DriverLeaveSummaryModel } from '@/app/models/timesheet/leaveSummaryModel'
import { YearMonthsValue } from '@/modules/drivers/types/date'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useTimesheetPrefsStore } from '@/modules/drivers/stores/timesheetStore'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'

interface TimesheetActionBarProps {
  variant?: 'group' | 'single'
  onCalculate?: () => void
  calculateBusy?: boolean
  calculateBusyText?: string
  onPrint?: () => void
  onExplain?: () => void
  onClear?: () => void
  onRefresh?: () => void
  onToggleOvertimeCompensation?: (enabled: boolean) => void
  onViewToggle?: (view: TimesheetViewMode) => void
  onRemove?: () => void
  onBonusAndReward?: () => void
  currentViewMode?: TimesheetViewMode
  period?: YearMonthsValue
  setPeriod?: (next: YearMonthsValue) => void
  hasCalculation?: boolean
  hasPreview?: boolean
  showOldTimesheet?: boolean
  onSave?: () => void
  onDiscard?: () => void
  onToggleOldNew?: () => void
  openAmounts?: () => void
  openForeignAmounts?: () => void
  showChart: boolean
  setShowChart: React.Dispatch<React.SetStateAction<boolean>>
  leaveSummary?: DriverLeaveSummaryModel | null
  printShortcuts?: React.ReactNode
}

const TimesheetActionBar: React.FC<TimesheetActionBarProps> = ({
  variant = 'group',
  onCalculate,
  calculateBusy,
  calculateBusyText,
  onPrint,
  onExplain,
  onClear,
  onRefresh,
  onToggleOvertimeCompensation,
  onViewToggle,
  onRemove,
  onBonusAndReward,
  openAmounts,
  currentViewMode,
  period,
  setPeriod,
  hasCalculation = false,
  hasPreview = false,
  showOldTimesheet = false,
  onSave,
  onDiscard,
  onToggleOldNew,
  showChart,
  setShowChart,
  openForeignAmounts,
  leaveSummary,
  printShortcuts,
}) => {
  const { t } = useLocalize()
  const { busyTooltip } = usePrintJobGuard()
  const storedViewMode = useTimesheetPrefsStore((s) => s.viewMode)
  const setStoredViewMode = useTimesheetPrefsStore((s) => s.setViewMode)
  const viewMode: TimesheetViewMode =
    storedViewMode === 'schedule' || storedViewMode === 'single-view'
      ? storedViewMode
      : 'single-view'
  const setViewMode = setStoredViewMode

  React.useEffect(() => {
    if (currentViewMode) {
      setStoredViewMode(currentViewMode)
    }
  }, [currentViewMode, setStoredViewMode])

  const handleViewToggle = (mode: TimesheetViewMode) => {
    setViewMode(mode)
    onViewToggle?.(mode)
  }

  return (
    <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Tooltip portal
            text={t('timesheet.view-single')}
            showDot
            onClick={() => handleViewToggle('single-view')}
            checked={viewMode === 'single-view'}
          >
            <div
              className={`tw-relative tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-px-2 tw-py-1`}
            >
              <FaIcon
                icon={faTable}
                className={`tw-h-4 tw-w-4 ${
                  viewMode === 'single-view'
                    ? 'tw-text-[#2448bb]'
                    : 'tw-text-[#6b77a0]'
                }`}
              />
              {viewMode === 'single-view' && (
                <div className="tw-absolute tw-bottom-0 tw-left-1/2 tw-h-[3px] tw-w-[3px] -tw-translate-x-1/2 tw-translate-y-[6px] tw-rounded-[4px] tw-bg-[#2448bb]"></div>
              )}
            </div>
          </Tooltip>
          <Tooltip portal
            text={t('timesheet.view-schedule')}
            showDot
            onClick={() => handleViewToggle('schedule')}
            checked={viewMode === 'schedule'}
          >
            <div
              className={`tw-relative tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-px-2 tw-py-1`}
            >
              <FaIcon
                icon={faCalendar}
                className={`tw-h-4 tw-w-4 ${
                  viewMode === 'schedule'
                    ? 'tw-text-[#2448bb]'
                    : 'tw-text-[#6b77a0]'
                }`}
              />
              {viewMode === 'schedule' && (
                <div className="tw-absolute tw-bottom-0 tw-left-1/2 tw-h-[3px] tw-w-[3px] -tw-translate-x-1/2 tw-translate-y-[6px] tw-rounded-[4px] tw-bg-[#2448bb]"></div>
              )}
            </div>
          </Tooltip>
        </div>

        {hasCalculation && (
          <>
            <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]"></div>
            <Tooltip portal
              text={
                calculateBusy
                  ? calculateBusyText || t('timesheet.calculation-in-progress')
                  : t('timesheet.calculate')
              }
              onClick={calculateBusy ? undefined : onCalculate}
            >
              <div
                className={
                  calculateBusy
                    ? 'tw-flex tw-h-4 tw-w-4 tw-cursor-not-allowed tw-items-center tw-justify-center tw-rounded-lg tw-opacity-40'
                    : 'tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]'
                }
              >
                <FaIcon
                  icon={faCalculator}
                  className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
                />
              </div>
            </Tooltip>
            <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]"></div>

            <Tooltip portal
              text={t('timesheet.bonus-and-reward')}
              align="start"
              onClick={onBonusAndReward}
            >
              <div className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faMoneyBillTrendUp}
                  className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
                />
              </div>
            </Tooltip>

            {/* Pogląd kwot - Amount view */}
            <Tooltip portal
              text={t('timesheet.amount-view')}
              onClick={() => openAmounts?.()}
            >
              <div className="tw-relative tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faCoins}
                  className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
                />
                <div className="tw-absolute -tw-right-1 -tw-top-1 tw-flex tw-h-[14px] tw-w-[20px] tw-items-center tw-justify-center tw-rounded-[8px] tw-border tw-border-[#fafbff] tw-bg-[#2448bb]">
                  <span className="tw-text-[8px] tw-font-medium tw-leading-[10px] tw-text-[#f1f4ff]">
                    PLN
                  </span>
                </div>
              </div>
            </Tooltip>

            {/* Kwoty zagraniczne - Foreign amounts */}
            <Tooltip portal
              text={t('timesheet.foreign-amounts')}
              onClick={() => openForeignAmounts?.()}
            >
              <div className="tw-relative tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faCoins}
                  className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
                />
                <div className="tw-absolute -tw-right-1 -tw-top-1 tw-flex tw-h-[14px] tw-w-[20px] tw-items-center tw-justify-center tw-rounded-[8px] tw-border tw-border-[#fafbff] tw-bg-[#2448bb]">
                  <span className="tw-text-[8px] tw-font-medium tw-leading-[10px] tw-text-[#f1f4ff]">
                    EUR
                  </span>
                </div>
              </div>
            </Tooltip>

            {/* Divider - -- */}
            <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]"></div>

            <Tooltip portal text={t('timesheet.toggle-chart')} onClick={() => setShowChart(!showChart)}>
              <div className={`pointer-events-none tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-[2px] ${showChart ? 'tw-border-[#2448bb] tw-bg-[#2448bb]' : 'tw-border-[#6b77a0]/50 tw-bg-[#6b77a0]/20'}`}>
                <FaIcon
                  icon={faChartBulletSolid}
                  className="tw-h-4 tw-w-4 tw-text-white"
                />
              </div>
            </Tooltip>

            {/* Pokaż/ukryj wartości przed zmianą - Show/hide values before change */}
            <Tooltip portal
              text={t('timesheet.toggle-values-before-change')}
              disabled={!hasPreview}
              onClick={hasPreview ? onToggleOldNew : undefined}
              checked={hasPreview}
            >
              <div
                className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-[2px] tw-border ${
                  hasPreview
                    ? showOldTimesheet
                      ? 'tw-border-[#2448bb] tw-bg-[#2448bb]'
                      : 'tw-border-[#6b77a0]/50 tw-bg-[#6b77a0]/20'
                    : 'pointer-events-none tw-border-[#6b77a0]/30 tw-bg-[#6b77a0]/30'
                }`}
              >
                <FaIcon
                  icon={faClockRotateLeft}
                  className={`tw-h-4 tw-w-4 ${hasPreview ? 'tw-text-[#6b77a0]' : 'tw-text-white'} ${showOldTimesheet ? 'tw-text-white' : ''}`}
                />
              </div>
            </Tooltip>

            {hasPreview && (
              <>
                <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]"></div>
                <Tooltip portal text={t('timesheet.discard')} onClick={onDiscard}>
                  <div className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded tw-bg-[#c0392b]">
                    <FaIcon
                      icon={faClose}
                      className="tw-h-2 tw-w-2 tw-text-white"
                    />
                  </div>
                </Tooltip>
                <Tooltip portal text={t('timesheet.save')} onClick={onSave}>
                  <div className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                    <FaIcon
                      icon={faFloppyDisk}
                      className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
                    />
                  </div>
                </Tooltip>
              </>
            )}
          </>
        )}
      </div>

      <div className="tw-flex tw-items-center tw-gap-4">
        {leaveSummary && (
          <>
            <TimesheetLeaveCounters summary={leaveSummary} />
            <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]"></div>
          </>
        )}
        {onExplain && (
          <Tooltip portal text={t('explain.action.tooltip')} onClick={onExplain}>
            <div className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
              <FaIcon
                icon={faCircleInfo}
                className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
              />
            </div>
          </Tooltip>
        )}
        <Tooltip portal
          text={t('common.print')}
          onClick={onPrint}
          blockedText={busyTooltip}
        >
          <div className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faPrint}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
            <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
              {t('common.print')}
            </span>
          </div>
        </Tooltip>
        {printShortcuts}

        <Tooltip portal text={t('timesheet.delete-timesheet')} onClick={onRemove}>
          <div className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
            <FaIcon icon={faTrash} className="tw-h-4 tw-w-4 tw-text-red-500" />
            <span className="tw-text-sm tw-font-medium tw-text-red-500">
              {t('timesheet.delete-timesheets')}
            </span>
          </div>
        </Tooltip>

        <Tooltip portal text={t('timesheet.refresh')} onClick={onRefresh}>
          <div className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faArrowsRotate}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

export default TimesheetActionBar
